import { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import ProductSchema from "../components/ProductSchema";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../supabaseClient";

import CosmicBackground from "../components/CosmicBackground";
import HeroSection from "../components/HeroSection";
import GlobalTrustPoints from "../components/global/GlobalTrustPoints";
import UgcReels from "../components/india/UgcReels";
import GlobalJourney from "../components/global/GlobalJourney";
import GlobalSteps from "../components/global/GlobalSteps";
import GlobalUseCases from "../components/global/GlobalUseCases";
import GlobalShop from "../components/global/GlobalShop";
import FeaturesSection from "../components/FeaturesSection";
import KundliSection from "../components/KundliSection";
import FaqSection, { defaultFaqData } from "../components/FaqSection";
import TestimonialsSection from "../components/TestimonialsSection";
import { internationalTestimonials } from "../data/internationalTestimonials";
import MobileAppSection from "../components/MobileAppSection";
import AuthModal from "../components/AuthModal";
import {
  IconCalendarStats,
  IconBook2,
  IconMessages,
  IconLock,
  IconGift,
} from "@tabler/icons-react";
import styles from "./MarketPage.module.css";
import { SHOW_STORE } from "../utils/featureFlags";

const PENDING_KUNDLI_DETAILS_KEY = "pendingKundliDetails";

declare global {
  interface Window {
    Razorpay: any;
  }
}

/**
 * /market landing page. Mirrors the homepage sections + behaviour (auth, the
 * pending-Kundli post-login flow, notification prompt, push re-registration,
 * hash scroll) with the marketing copy/sections layered on top.
 *
 * Also serves /india in `focused` mode, which is the same page with the
 * calculator dropped. Paired with the nav hiding Blog and Calculators on that
 * route, it leaves one path through the page instead of several exits. Same
 * component rather than a copy, so the two cannot drift apart.
 */
export default function MarketPage({ focused = false }: { focused?: boolean } = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const faqRef = useRef<HTMLElement>(null);

  const {
    user,
    signInWithGoogle,
    requestPhoneOtp,
    verifyPhoneOtp,
    requestFirebasePhoneOtp,
    verifyFirebasePhoneOtp,
    verifyMsg91Otp,
    planTier,
    checkingStatus,
    pricingVariant,
    refreshUserStatus,
    userProfiles,
  } = useAuth();

  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  // ---- Auth handlers (identical to HomePage, incl. A/B pricing variant) ----
  const phoneAuthHandlers = useMemo(
    () => ({ requestOtp: requestPhoneOtp, verifyOtp: verifyPhoneOtp }),
    [requestPhoneOtp, verifyPhoneOtp]
  );

  const firebasePhoneAuthHandlers = useMemo(
    () => ({
      requestOtp: requestFirebasePhoneOtp,
      verifyOtp: verifyFirebasePhoneOtp,
    }),
    [requestFirebasePhoneOtp, verifyFirebasePhoneOtp]
  );

  const msg91AuthHandlers = useMemo(
    () => ({
      verifyOtp: (payload: {
        accessToken: string;
        phone: string;
        firstName?: string;
        lastName?: string;
        email?: string;
      }) => verifyMsg91Otp({ ...payload, variant: pricingVariant }),
    }),
    [verifyMsg91Otp, pricingVariant]
  );

  // ---- Mark this session as the "market" experience ----
  // Lets shared pages (e.g. /reports) and the nav Home link render the /market
  // variant for users who entered via /market, without touching the main site.
  useEffect(() => {
    try {
      sessionStorage.setItem("auraMode", "market");
      /* And record the landing path, so "back" on /india returns to /india
         rather than to /market. auraMode stays "market" because the shared
         pages (/reports and friends) key their variant off that name, and
         /india is the same experience under a new URL. */
      sessionStorage.setItem("auraLanding", focused ? "/india" : "/market");
    } catch {
      /* sessionStorage unavailable */
    }
    // body class scopes the attention-pulse to the hero + Kundli CTAs here only
    document.body.classList.add("market-mode");
    return () => document.body.classList.remove("market-mode");
  }, [focused]);

  // ---- Scroll to #faq when linked ----
  useEffect(() => {
    if (location.hash === "#faq" && faqRef.current) {
      faqRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [location]);

  // ---- Create the pending Kundli profile after the user signs in ----
  useEffect(() => {
    if (user && !checkingStatus && userProfiles) {
      const pendingDetailsJSON = sessionStorage.getItem(
        PENDING_KUNDLI_DETAILS_KEY
      );

      if (pendingDetailsJSON) {
        // Multiple Kundli profiles are allowed for everyone — no subscription
        // required — so we proceed straight to creating the profile.
        sessionStorage.removeItem(PENDING_KUNDLI_DETAILS_KEY);

        const createPendingProfile = async () => {
          try {
            const details = JSON.parse(pendingDetailsJSON);

            const { data: profileData, error: profileError } = await supabase
              .from("user_profiles")
              .insert({ name: details.name, user_id: user.id })
              .select()
              .single();

            if (profileError) throw profileError;

            const { error: birthDetailsError } = await supabase
              .from("user_birth_details")
              .insert({
                profile_id: profileData.id,
                date_of_birth: details.date_of_birth,
                time_of_birth: details.time_of_birth,
                gender: details.gender,
                birth_place: details.birth_place,
                birth_lat: details.birth_lat,
                birth_lng: details.birth_lng,
                birth_timezone: details.birth_timezone,
              });

            if (birthDetailsError) throw birthDetailsError;

            await refreshUserStatus();
            navigate("/reports");
          } catch (error) {
            console.error("Failed to save pending Kundli details:", error);
            alert(
              "There was an issue creating your Kundli profile. Please try again from the homepage."
            );
          }
        };

        createPendingProfile();
      }
    }
  }, [
    user,
    checkingStatus,
    userProfiles,
    planTier,
    navigate,
    refreshUserStatus,
  ]);


  // ---- Silent push re-registration for users who already granted permission ----
  useEffect(() => {
    const reRegisterExistingUsers = async () => {
      // @ts-ignore - CleverTap types not available
      const clevertap = (window as any).clevertap;

      const hasReRegistered = localStorage.getItem("ct_push_reregistered");
      if (hasReRegistered) return;

      if (
        typeof Notification === "undefined" ||
        Notification.permission !== "granted"
      ) {
        return;
      }

      if (!clevertap?.notifications?.push) return;

      try {
        await clevertap?.notifications.push({
          titleText: "Notifications Updated",
          bodyText: "Your notification settings have been improved.",
          skipDialog: true,
          serviceWorkerPath: "/clevertap_sw.js",
          vapidPublicKey:
            "BBP6tWBZCnyNnN39wXWHaKJP_K2WbDM4034HIr2Md_aTb36mqkkldoFP_rxh55v-_G1rIxSZE0Q6rEMXIT5pVuo",
        });

        localStorage.setItem("ct_push_reregistered", "true");

        if (clevertap?.profile?.push) {
          clevertap?.profile.push({
            Site: {
              push_enabled: true,
              re_registered: true,
              re_registered_at: new Date().toISOString(),
            },
          });
        }
      } catch (error) {
        console.warn("[Re-registration] Failed to re-register user:", error);
      }
    };

    const timer = setTimeout(() => {
      reRegisterExistingUsers();
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  // Keep the first two homepage FAQs, replace the rest with the boss copy.
  const marketFaq = useMemo(
    () => [
      defaultFaqData[0],
      defaultFaqData[1],
      {
        question: "Is this only a daily horoscope app?",
        answer:
          "No. Aura AI is more than daily horoscope. It lets you ask personal questions and get answers based on your birth chart.",
        icon: <IconCalendarStats size={20} />,
      },
      {
        question: "Do I need astrology knowledge?",
        answer: "No. Aura explains everything in simple language.",
        icon: <IconBook2 size={20} />,
      },
      {
        question: "Can I ask follow-up questions?",
        answer:
          "Yes. You can continue the conversation and ask follow-up questions.",
        icon: <IconMessages size={20} />,
      },
      {
        question: "Is my data private?",
        answer:
          "Yes. Your birth details and personal questions are kept private and secure.",
        icon: <IconLock size={20} />,
      },
      {
        question: "Is the first chat free?",
        answer: "Yes. You can start with your first free question.",
        icon: <IconGift size={20} />,
      },
    ],
    []
  );

  return (
    <main
      className={styles.market}
      style={{ position: "relative", isolation: "isolate" }}
    >
      <ProductSchema />
      <Helmet>
        <title>Ask Aura AI: Your Personal AI Vedic Astrologer | Aura AI</title>
        <meta
          name="description"
          content="Ask Aura AI about love, marriage, career or money and get instant Vedic astrology guidance read from your own birth chart. First question free."
        />
        {/* Self-referencing per route. /india is a distinct page, not a
            duplicate of /market, so pointing both at /market would ask Google
            to drop the one we are sending campaign traffic to. */}
        <link
          rel="canonical"
          href={`https://astroaura.ai${focused ? "/india/" : "/market"}`}
        />
        <meta property="og:image" content="https://astroaura.ai/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://astroaura.ai/og-image.png" />
      </Helmet>

      <CosmicBackground />

      <div style={{ position: "relative", width: "100%", zIndex: 1 }}>
        {/* /india is the page the paid traffic lands on, so it gets the
            direct hook and leads with the privacy line; /market keeps the
            plain heading and the default order. */}
        <HeroSection
          user={user}
          onGoogleSignIn={signInWithGoogle}
          phoneAuth={phoneAuthHandlers}
          msg91Auth={msg91AuthHandlers}
          headingLead={focused ? "Stop Guessing About" : "Ask Aura AI,"}
          headingAccent={focused ? "Your Own Life" : "Your AI Astrologer"}
          points={
            focused
              ? [
                  "Safe. Private. Encrypted.",
                  "10M+ charts read, 10K+ classical Vedic rules",
                  "Real Vedic Astrology. Instant Answers.",
                ]
              : undefined
          }
        />
      </div>

      <div
        style={{
          backgroundColor: "transparent",
          marginTop: "-1px",
          position: "relative",
          zIndex: 1,
        }}
      >
        <FeaturesSection withImages />
      </div>

      {/* E-COMMERCE / SHOP. Hidden with the store, and never on /india: the
          shop is a second destination, and that page is meant to have one. */}
      {SHOW_STORE && !focused && (
        <GlobalShop onShopAll={() => navigate("/store")} />
      )}

      {/* PROBLEM + SOLUTION (question at night) */}
      <GlobalJourney />

      {/* HOW IT WORKS */}
      <GlobalSteps />

      {/* The calculator is the page's one interactive detour. /india drops it
          so the page has a single call to action rather than a free tool that
          ends somewhere else. */}
      {!focused && (
        <div
          style={{
            backgroundColor: "transparent",
            marginTop: "-1px",
            position: "relative",
            zIndex: 1,
          }}
        >
          <KundliSection />
        </div>
      )}

      {/* TRUST POINTS */}
      <GlobalTrustPoints />

      {/* USE CASES (ask aura) */}
      <GlobalUseCases />

      <div
        style={{
          backgroundColor: "transparent",
          marginTop: "-1px",
          position: "relative",
          zIndex: 1,
        }}
      >
        <section ref={faqRef}>
          <FaqSection items={marketFaq} />
        </section>
      </div>

      {/* Creator reels, /india only. Sits directly above the written
          testimonials so the video proof leads and the quotes back it up. */}
      {focused && <UgcReels />}

      <div
        style={{
          backgroundColor: "transparent",
          marginTop: "-1px",
          position: "relative",
          zIndex: 1,
        }}
      >
        <TestimonialsSection
          items={internationalTestimonials}
          title="Trusted by people looking for real clarity"
          subtitle="Thousands of users are using Aura AI to understand their relationships, career, marriage, family situations, and life path with personalized astrology guidance."
          microcopy="Start with one free question and experience the clarity yourself"
        />
      </div>

      <div
        style={{
          backgroundColor: "transparent",
          marginTop: "-35px",
          position: "relative",
          zIndex: 1,
        }}
      >
        <MobileAppSection
          title={<>Your Personal Astrologer is Now in Your Pocket</>}
          subtitle={
            <>
              Download Aura AI and ask your first question for free. Whether it
              is 2 AM confusion, relationship doubt, career stress, or marriage
              pressure, Aura is available anytime.
            </>
          }
        />
      </div>

      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        onGoogleSignIn={signInWithGoogle}
        phoneAuth={phoneAuthHandlers}
        firebasePhoneAuth={firebasePhoneAuthHandlers}
        msg91Auth={msg91AuthHandlers}
      />
    </main>
  );
}
